import React, { useEffect } from 'react';
import { Permissions, useAuth } from '@kp/react-sdk';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useBreadcrumb } from '../../../contexts/breadcrumb-context';
import { entityBreadcrumbLabel } from '../../../utils/breadcrumb';
import { useDeviceModelsListQuery } from '../../../__generated__/types';
import * as Entities from '../../../constants/Entities';
import { DeviceModelsList } from './DeviceModelsList';
import { ErrorMessage } from '../../../components/Errors';
import { entityLocation } from '../../../utils/entity';
import { useFilters } from '../../../hooks/filters';
import { useHeader } from '../../../contexts/header-context';

export const DeviceModelsListContainer: React.FC = () => {
  const { t } = useTranslation(['deviceModels', 'general']);
  const navigate = useNavigate();
  const { page, pageSize, query, setPage, setPageSize, setQuery } =
    useFilters();
  const { setTitle } = useHeader();
  const { hasPermission } = useAuth();

  const handleAdd = () => navigate(entityLocation(Entities.DEVICEMODEL, `new`));

  const { loading, error, data } = useDeviceModelsListQuery({
    variables: {
      take: pageSize,
      skip: page * pageSize,
      where: {
        name: { contains: query },
      },
    },
  });

  useBreadcrumb({
    title: entityBreadcrumbLabel(Entities.DEVICEMODEL, t),
    location: Entities.DEVICEMODEL.path,
  });

  const deviceModels = data?.deviceModels?.items ?? [];
  const count = data?.deviceModels?.totalCount ?? 0;

  useEffect(() => {
    setTitle({ main: t('deviceModels:list.title') });
  }, [setTitle, t]);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <DeviceModelsList
      loading={loading}
      page={page}
      pageSize={pageSize}
      count={count}
      onChangePageSize={setPageSize}
      onChangePage={setPage}
      searchValue={query}
      onSearchSubmit={setQuery}
      deviceModels={deviceModels}
      onAdd={
        hasPermission(Permissions.DeviceModelsWrite) ? handleAdd : undefined
      }
    />
  );
};
